import { Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei';
import { RotateCcw, Play, Pause, Camera } from 'lucide-react';
import { useState } from 'react';
import * as THREE from 'three';

function Mannequin() {
  const ref = useRef<THREE.Group>(null);
  
  return (
    <group ref={ref} position={[0, -1, 0]}>
      {/* Simple mannequin placeholder - will be replaced with actual model */}
      <mesh position={[0, 1.5, 0]}>
        <capsuleGeometry args={[0.15, 0.3, 4, 8]} />
        <meshStandardMaterial color="#52525b" />
      </mesh>
      {/* Torso */}
      <mesh position={[0, 0.8, 0]}>
        <capsuleGeometry args={[0.25, 0.5, 4, 8]} />
        <meshStandardMaterial color="#52525b" />
      </mesh>
      {/* Hips */}
      <mesh position={[0, 0.2, 0]}>
        <capsuleGeometry args={[0.22, 0.3, 4, 8]} />
        <meshStandardMaterial color="#52525b" />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.12, -0.5, 0]}>
        <capsuleGeometry args={[0.08, 0.6, 4, 8]} />
        <meshStandardMaterial color="#52525b" />
      </mesh>
      <mesh position={[0.12, -0.5, 0]}>
        <capsuleGeometry args={[0.08, 0.6, 4, 8]} />
        <meshStandardMaterial color="#52525b" />
      </mesh>
    </group>
  );
}

function GarmentMesh() {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (meshRef.current) {
      // Subtle breathing animation
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.01;
    }
  });

  return (
    <mesh ref={meshRef} position={[0, 0.5, 0.15]}>
      {/* Placeholder garment - will load actual simulated mesh */}
      <boxGeometry args={[0.5, 0.6, 0.1]} />
      <meshStandardMaterial color="#0c8ee6" transparent opacity={0.8} />
    </mesh>
  );
}

export function Viewer3D() {
  const [isRotating, setIsRotating] = useState(false);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsRotating(!isRotating)}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            {isRotating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
        <button className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors">
          <Camera className="w-4 h-4" />
        </button>
      </div>

      {/* 3D Canvas */}
      <div className="flex-1 viewer-3d">
        <Canvas
          camera={{ position: [0, 0, 3], fov: 50 }}
          gl={{ antialias: true }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.4} />
            <spotLight position={[5, 5, 5]} angle={0.3} penumbra={1} intensity={0.5} />
            <spotLight position={[-5, 5, 5]} angle={0.3} penumbra={1} intensity={0.3} />
            
            <Mannequin />
            <GarmentMesh />
            
            <ContactShadows
              position={[0, -1.2, 0]}
              opacity={0.4}
              scale={3}
              blur={2}
              far={4}
            />
            <Environment preset="studio" />
            <OrbitControls
              autoRotate={isRotating}
              autoRotateSpeed={1}
              enablePan={false}
              minPolarAngle={Math.PI / 4}
              maxPolarAngle={Math.PI / 1.5}
            />
          </Suspense>
        </Canvas>
      </div>

      {/* Info panel */}
      <div className="border-t border-zinc-800 p-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500">Drape simulation</span>
          <span className="text-green-400">Ready</span>
        </div>
      </div>
    </div>
  );
}

